import * as math from 'mathjs';
import { sanitizeExpression, extractLogArguments } from '../sanitizeExpression';

function evalReal(fn, x) {
  try {
    const v = fn.evaluate({ x });
    return typeof v === 'number' && Number.isFinite(v) ? v : NaN;
  } catch {
    return NaN;
  }
}

// Miejsca zerowe argumentu logarytmu = pionowe asymptoty.
function findArgumentZeros(argExpr, xMin, xMax) {
  let fn;
  try { fn = math.compile(sanitizeExpression(argExpr)); } catch { return []; }
  const zeros = [];
  const pushZero = (root) => {
    const r = Math.round(root * 10000) / 10000;
    if (r < xMin || r > xMax) return;
    if (!zeros.some((z) => Math.abs(z - r) < 1e-3)) zeros.push(r);
  };
  const scanStep = Math.max((xMax - xMin) / 4000, 1e-4);
  let prevX = xMin;
  let prevV = evalReal(fn, xMin);
  if (Number.isFinite(prevV) && Math.abs(prevV) < 1e-9) pushZero(prevX);
  for (let x = xMin + scanStep; x <= xMax; x += scanStep) {
    const v = evalReal(fn, x);
    if (Number.isFinite(v) && Number.isFinite(prevV) && prevV !== 0 && Math.sign(prevV) !== Math.sign(v)) {
      let a = prevX;
      let b = x;
      let fa = prevV;
      for (let i = 0; i < 40; i += 1) {
        const m = (a + b) / 2;
        const fm = evalReal(fn, m);
        if (!Number.isFinite(fm)) break;
        if (Math.abs(fm) < 1e-10) { a = m; b = m; break; }
        if (Math.sign(fa) !== Math.sign(fm)) { b = m; } else { a = m; fa = fm; }
      }
      pushZero((a + b) / 2);
    }
    prevX = x;
    prevV = v;
  }
  return zeros;
}

// Znajduje x w [xa, xb], gdzie fn(x) ≈ target (jeden koniec w zakresie, drugi poza).
function boundaryCrossing(fn, xa, xb, target) {
  let a = xa;
  let b = xb;
  let fa = evalReal(fn, a) - target;
  for (let i = 0; i < 40; i += 1) {
    const m = (a + b) / 2;
    const fm = evalReal(fn, m);
    if (!Number.isFinite(fm)) { b = m; continue; }
    const d = fm - target;
    if (Math.abs(d) < 1e-6) { a = m; b = m; break; }
    if (Number.isFinite(fa) && Math.sign(fa) !== Math.sign(d)) { b = m; } else { a = m; fa = d; }
  }
  return (a + b) / 2;
}

export function generateLogarithmicData(equation, xMin, xMax, yMin, yMax, step) {
  let fn;
  try { fn = math.compile(sanitizeExpression(equation)); } catch { return { segments: [], asymptotes: [] }; }

  const asymptotes = [];
  extractLogArguments(equation).forEach((argExpr) => {
    findArgumentZeros(argExpr, xMin, xMax).forEach((z) => {
      if (!asymptotes.some((a) => Math.abs(a - z) < 1e-3)) asymptotes.push(z);
    });
  });

  const margin = (yMax - yMin) * 0.2;
  const hardYMin = yMin - margin;
  const hardYMax = yMax + margin;

  const segments = [];
  let xs = [];
  let ys = [];
  const flush = () => {
    if (xs.length > 1) segments.push({ x: xs, y: ys });
    xs = [];
    ys = [];
  };

  let prevX = null;
  let prevVal = NaN;
  for (let xi = xMin; xi <= xMax + 1e-9; xi += step) {
    const val = evalReal(fn, xi);
    const inRange = Number.isFinite(val) && val >= hardYMin && val <= hardYMax;

    if (inRange) {
      // wejście z obszaru przekraczającego zakres (skok ku asymptocie) -> punkt na krawędzi
      if (xs.length === 0 && prevX !== null && Number.isFinite(prevVal) && (prevVal > hardYMax || prevVal < hardYMin)) {
        const target = prevVal > hardYMax ? hardYMax : hardYMin;
        const xc = boundaryCrossing(fn, xi, prevX, target);
        if (Number.isFinite(xc)) { xs.push(xc); ys.push(target); }
      }
      xs.push(xi);
      ys.push(val);
    } else {
      // wyjście poza zakres (ku asymptocie) -> domknij segment punktem na krawędzi
      if (xs.length > 0 && Number.isFinite(val) && (val > hardYMax || val < hardYMin) && prevX !== null) {
        const target = val > hardYMax ? hardYMax : hardYMin;
        const xc = boundaryCrossing(fn, prevX, xi, target);
        if (Number.isFinite(xc)) { xs.push(xc); ys.push(target); }
      }
      flush();
    }
    prevX = xi;
    prevVal = val;
  }
  flush();

  return { segments, asymptotes: asymptotes.sort((a, b) => a - b) };
}
